import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import {
  RELATIONSHIP_TYPES,
  type PersonRelationshipResponse,
  type RelationshipType,
} from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { peopleApi } from '@/services/people.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button } from '@/components/ui';
import { PersonPicker } from './person-picker';

/**
 * A person's directed relationships to other people (guardian, spouse,
 * sibling, ...) — see `PersonRelationship` for why these are stored once,
 * with an inverse, rather than as a pair of rows that could disagree.
 */
export function RelationshipsCard({
  personId,
  relationships,
  onChanged,
}: {
  readonly personId: string;
  readonly relationships: readonly PersonRelationshipResponse[];
  readonly onChanged: () => void;
}) {
  const { t } = useI18n();
  const { can } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [relationshipType, setRelationshipType] = useState<RelationshipType>('OTHER');

  const create = useMutation({
    mutationFn: (input: { relatedPersonId: string; relationshipType: RelationshipType }) =>
      peopleApi.createRelationship(personId, {
        relatedPersonId: input.relatedPersonId,
        relationshipType: input.relationshipType,
      }),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (err: unknown) => setError(describeError(err, t)),
  });

  const remove = useMutation({
    mutationFn: (relationshipId: string) => peopleApi.deleteRelationship(relationshipId),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (err: unknown) => setError(describeError(err, t)),
  });

  if (!can('people.relationship.read')) return null;

  return (
    <section className="detail-section">
      <h2 className="detail-section-title">{t('people.relationship.title')}</h2>

      {error && <Alert tone="danger">{error}</Alert>}

      {relationships.length === 0 ? (
        <p className="cell-empty">{t('common.states.empty')}</p>
      ) : (
        <ul className="people-relation-list">
          {relationships.map((relationship) => (
            <li key={relationship.id} className="member-row">
              <Link to={`/people/${relationship.relatedPersonId}`}>
                {relationship.relatedPersonDisplayName}
              </Link>
              <span className="cell-mono">
                {t(`people.relationship.type.${relationship.relationshipType}`)}
              </span>
              {can('people.relationship.delete') && (
                <Button
                  variant="ghost"
                  loading={remove.isPending && remove.variables === relationship.id}
                  onClick={() => remove.mutate(relationship.id)}
                >
                  {t('common.actions.delete')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {can('people.relationship.create') && (
        <div className="form-grid">
          <div className="field">
            <label className="field-label" htmlFor="relationship-type">
              {t('people.relationship.typeLabel')}
            </label>
            <select
              id="relationship-type"
              className="field-input"
              value={relationshipType}
              onChange={(event) => setRelationshipType(event.target.value as RelationshipType)}
            >
              {RELATIONSHIP_TYPES.map((value) => (
                <option key={value} value={value}>
                  {t(`people.relationship.type.${value}`)}
                </option>
              ))}
            </select>
          </div>
          <PersonPicker
            label={t('people.relationship.add')}
            placeholder={t('groups.group.choosePerson')}
            exclude={[personId, ...relationships.map((r) => r.relatedPersonId)]}
            onPick={(relatedPersonId) => create.mutate({ relatedPersonId, relationshipType })}
          />
        </div>
      )}
    </section>
  );
}

function describeError(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}
